import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthDispatch, useAuthState } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import MiniWishlist from './MiniWishlist';
import CartDrawer from './CartDrawer';
import SearchAutocomplete from './SearchAutocomplete';
import { normalizeSearchQuery, saveRecentSearch } from '../utils/searchUtils';
import { getUserInitials } from '../utils/userDisplay';

function Header() {
  const { user, isAuthenticated } = useAuthState();
  const { logout } = useAuthDispatch();
  const location = useLocation();
  const { cart } = useCart();
  const { wishlist } = useWishlist();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileMenuRendered, setIsMobileMenuRendered] = useState(false);
  const [isMobileMenuClosing, setIsMobileMenuClosing] = useState(false);
  const [showWishlistPanel, setShowWishlistPanel] = useState(false);
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const wishlistRef = useRef(null);
  const wishlistDropdownRef = useRef(null);
  const accountRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuTriggerRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const searchInputRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const headerRootRef = useRef(null);
  const rafRef = useRef(null);
  const MOBILE_DRAWER_ANIM_MS = 250;

  const handleCartClick = (e) => {
    // Authenticated: open cart drawer (route still reachable from drawer)
    e.preventDefault();
    setShowCartDrawer(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openMobileMenu = () => {
    lastFocusedRef.current = document.activeElement;
    setIsMobileMenuRendered(true);
    setIsMobileMenuClosing(false);
    setIsMobileMenuOpen(true);
  };

  const closeMobileMenu = useCallback(() => {
    if (!isMobileMenuRendered || isMobileMenuClosing) return;
    setIsMobileMenuOpen(false);
    setIsMobileMenuClosing(true);
    setTimeout(() => {
      setIsMobileMenuRendered(false);
      setIsMobileMenuClosing(false);
      const el = lastFocusedRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    }, MOBILE_DRAWER_ANIM_MS);
  }, [isMobileMenuRendered, isMobileMenuClosing, MOBILE_DRAWER_ANIM_MS]);

  // Mobile menu: scroll lock, click outside, Esc, focus trap
  useEffect(() => {
    if (!isMobileMenuRendered) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
        return;
      }

      if (event.key !== 'Tab') return;
      if (!mobileMenuRef.current) return;

      const focusables = mobileMenuRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === mobileMenuRef.current) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    const handleMouseDown = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    setTimeout(() => {
      if (mobileMenuRef.current) {
        const firstFocusable = mobileMenuRef.current.querySelector(
          'button, a[href], input, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable && typeof firstFocusable.focus === 'function') {
          firstFocusable.focus();
        }
      }
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuRendered, closeMobileMenu]);

  // Close wishlist panel when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wishlistRef.current &&
        wishlistDropdownRef.current &&
        !wishlistRef.current.contains(event.target) &&
        !wishlistDropdownRef.current.contains(event.target)
      ) {
        setShowWishlistPanel(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && showWishlistPanel) {
        setShowWishlistPanel(false);
      }
    };

    if (showWishlistPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      
      // Focus trap: focus the dropdown when it opens
      setTimeout(() => {
        if (wishlistDropdownRef.current) {
          wishlistDropdownRef.current.focus();
        }
      }, 0);
      
    } else {
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showWishlistPanel]);

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newState = !showWishlistPanel;
    setShowWishlistPanel(newState);
    if (newState) setShowAccountMenu(false);
  };

  const handleWishlistClose = () => {
    setShowWishlistPanel(false);
  };

  const handleWishlistItemClick = () => {
    setShowWishlistPanel(false);
  };

  // Close account menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accountRef.current &&
        accountDropdownRef.current &&
        !accountRef.current.contains(event.target) &&
        !accountDropdownRef.current.contains(event.target)
      ) {
        setShowAccountMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && showAccountMenu) {
        setShowAccountMenu(false);
      }
    };

    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      setTimeout(() => {
        if (accountDropdownRef.current) accountDropdownRef.current.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showAccountMenu]);

  const handleAccountToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Logged-out: direct route to login (no drawer/menu).
    if (!isAuthenticated) {
      setShowWishlistPanel(false);
      setShowAccountMenu(false);
      setIsMobileMenuOpen(false);
      setIsMobileMenuClosing(false);
      setIsMobileMenuRendered(false);
      navigate('/login');
      return;
    }

    const next = !showAccountMenu;
    setShowAccountMenu(next);
    if (next) setShowWishlistPanel(false);
  };

  const closeAccountMenu = () => setShowAccountMenu(false);

  // Sticky behavior: elevate on scroll (always visible).
  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        const y = window.scrollY || 0;
        const scrolledNow = y > 8;
        setIsScrolled(scrolledNow);
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // initialize state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  // Lock body scroll when wishlist/account drawers are open
  useEffect(() => {
    if (!showAccountMenu && !showWishlistPanel) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showAccountMenu, showWishlistPanel]);

  // Hide header on admin routes
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <header ref={headerRootRef} className="ah-header-root">
      <nav
        className={[
          'navbar',
          'navbar-expand-lg',
          'navbar-dark',
          'ah-navbar',
          'sticky-top',
          isScrolled ? 'ah-navbar--scrolled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="container">
          {/* Mobile / small screens: logo + utility icons + hamburger, then search */}
          <div className="d-flex d-lg-none align-items-center justify-content-between w-100 ah-header-top">
            <Link className="navbar-brand d-flex align-items-center mx-lg-0" to="/" aria-label="AmbarHome home">
              <img src="/ambarlogo.png" alt="AmbarHome" className="navbar-logo" />
            </Link>

            <div className="d-flex align-items-center gap-1 ah-header-top-actions">
              <span className="ah-popover-anchor" ref={wishlistRef}>
                <button
                  className="ah-icon-btn position-relative text-light"
                  type="button"
                  onClick={handleWishlistToggle}
                  aria-label="Wishlist"
                  aria-expanded={showWishlistPanel}
                  title="Wishlist"
                >
                  <i className="bi bi-heart fs-5" aria-hidden="true" />
                  {wishlist.count > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill cart-badge" aria-label={`${wishlist.count} items in wishlist`}>
                      {wishlist.count}
                    </span>
                  )}
                </button>
              </span>

              <Link
                to="/cart"
                className="ah-icon-btn position-relative text-light"
                onClick={handleCartClick}
                aria-label="Shopping cart"
                title="Cart"
              >
                <i className="bi bi-cart3 fs-5" aria-hidden="true" />
                {cart.count > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill cart-badge" aria-label={`${cart.count} items in cart`}>
                    {cart.count}
                  </span>
                )}
              </Link>

              <span className="ah-popover-anchor" ref={accountRef}>
                <button
                  className="ah-icon-btn text-light"
                  type="button"
                  onClick={handleAccountToggle}
                  aria-label="Account"
                  aria-expanded={showAccountMenu}
                  title="Account"
                >
                  {isAuthenticated ? (
                    <span className="ah-header-account-avatar" aria-hidden="true">
                      {getUserInitials(user)}
                    </span>
                  ) : (
                    <i className="bi bi-person fs-5" aria-hidden="true" />
                  )}
                </button>
              </span>

              <button
                ref={mobileMenuTriggerRef}
                className="navbar-toggler border-0 d-lg-none"
                type="button"
                onClick={openMobileMenu}
                aria-controls="mobile-navigation-drawer"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon" />
              </button>
            </div>
          </div>

          {/* Mobile search (dominant, full-width) */}
          <form
            className="d-flex w-100 d-lg-none my-2 header-search-form"
            role="search"
            action="/products"
            onSubmit={(e) => {
              const q = normalizeSearchQuery(searchValue);
              if (q) saveRecentSearch(q);
            }}
          >
            <div className="input-group header-search-group">
              <input
                type="search"
                name="search"
                className="form-control header-search-input"
                placeholder="Search for parts, appliances, models..."
                aria-label="Search products"
                ref={searchInputRef}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                autoComplete="off"
              />
              <button className="btn btn-primary header-search-btn" type="submit" aria-label="Submit search">
                <i className="bi bi-search" aria-hidden="true" />
              </button>
            </div>
            <SearchAutocomplete
              value={searchValue}
              onValueChange={setSearchValue}
              onRequestClose={() => setSearchFocused(false)}
              inputRef={searchInputRef}
              searchFocused={searchFocused}
            />
          </form>

          {/* Desktop: requested priority
              Logo (left) → Search (dominant) → Wishlist/Cart/Account (right)
              "All Products" stays available but doesn't steal attention from search.
          */}
          <div className="d-none d-lg-flex align-items-center w-100 ah-header-desktop">
            <Link className="navbar-brand d-flex align-items-center mx-lg-0" to="/" aria-label="AmbarHome home">
              <img src="/ambarlogo.png" alt="AmbarHome" className="navbar-logo" />
            </Link>

            <form
              className="d-flex flex-grow-1 mx-3 header-search-form ah-header-search-desktop"
              role="search"
              action="/products"
              onSubmit={(e) => {
                const q = normalizeSearchQuery(searchValue);
                if (q) saveRecentSearch(q);
              }}
            >
              <div className="input-group header-search-group w-100">
                <input
                  type="search"
                  name="search"
                  className="form-control header-search-input"
                  placeholder="Search for parts, appliances, models..."
                  aria-label="Search products"
                  ref={searchInputRef}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  autoComplete="off"
                />
                <button className="btn btn-primary header-search-btn" type="submit" aria-label="Submit search">
                  <i className="bi bi-search" aria-hidden="true" />
                </button>
              </div>
              <SearchAutocomplete
                value={searchValue}
                onValueChange={setSearchValue}
                onRequestClose={() => setSearchFocused(false)}
                inputRef={searchInputRef}
                searchFocused={searchFocused}
              />
            </form>

            <div className="d-flex align-items-center gap-2 ah-header-desktop-right">
              <Link
                className={`nav-link ah-header-all-products ${location.pathname.startsWith('/products') ? 'active' : ''}`}
                to="/products"
              >
                All Products
              </Link>

              <span className="ah-popover-anchor" ref={wishlistRef}>
                <button
                  className="ah-icon-btn position-relative text-light"
                  type="button"
                  onClick={handleWishlistToggle}
                  aria-label="Wishlist"
                  aria-expanded={showWishlistPanel}
                  title="Wishlist"
                >
                  <i className="bi bi-heart fs-5" aria-hidden="true" />
                  {wishlist.count > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill cart-badge" aria-label={`${wishlist.count} items in wishlist`}>
                      {wishlist.count}
                    </span>
                  )}
                </button>
              </span>

              <Link
                to="/cart"
                className="ah-icon-btn position-relative text-light"
                onClick={handleCartClick}
                aria-label="Shopping cart"
                title="Cart"
              >
                <i className="bi bi-cart3 fs-5" aria-hidden="true" />
                {cart.count > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill cart-badge" aria-label={`${cart.count} items in cart`}>
                    {cart.count}
                  </span>
                )}
              </Link>

              <span className="ah-popover-anchor" ref={accountRef}>
                <button
                  className="ah-icon-btn text-light"
                  type="button"
                  onClick={handleAccountToggle}
                  aria-label="Account"
                  aria-expanded={showAccountMenu}
                  title="Account"
                >
                  {isAuthenticated ? (
                    <span className="ah-header-account-avatar" aria-hidden="true">
                      {getUserInitials(user)}
                    </span>
                  ) : (
                    <i className="bi bi-person fs-5" aria-hidden="true" />
                  )}
                </button>
              </span>
            </div>
          </div>
        </div>
      </nav>

      {showWishlistPanel && (
        <>
          <div
            className="ah-side-drawer-backdrop"
            onClick={handleWishlistClose}
            aria-hidden="true"
          />
          <div
            ref={wishlistDropdownRef}
            className="ah-side-drawer ah-side-drawer--right"
            role="dialog"
            aria-modal="true"
            aria-label="Wishlist items"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ah-side-drawer-header">
              <h2 className="ah-side-drawer-title">
                <i className="bi bi-heart me-2" aria-hidden="true" />
                Wishlist {wishlist.count > 0 && <span>({wishlist.count})</span>}
              </h2>
              <button className="ah-side-drawer-close" onClick={handleWishlistClose} aria-label="Close wishlist" type="button">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
            <div className="ah-side-drawer-body">
              <MiniWishlist onItemClick={handleWishlistItemClick} showHeader={false} />
            </div>
          </div>
        </>
      )}

      {showAccountMenu && (
        <>
          <div
            className="ah-side-drawer-backdrop"
            onClick={closeAccountMenu}
            aria-hidden="true"
          />
          <div
            ref={accountDropdownRef}
            className="ah-side-drawer ah-side-drawer--right"
            role="dialog"
            aria-modal="true"
            aria-label="Account menu"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ah-side-drawer-header">
              <h2 className="ah-side-drawer-title">
                <i className="bi bi-person me-2" aria-hidden="true" />
                {isAuthenticated ? (user?.name || 'My account') : 'Account'}
              </h2>
              <button className="ah-side-drawer-close" onClick={closeAccountMenu} aria-label="Close account menu" type="button">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>

            <div className="ah-side-drawer-body">
              <div className="ah-account-menu-body" role="menu" aria-label="Account actions">
              {isAuthenticated ? (
                <>
                  <Link to="/profile" className="ah-account-menu-item" role="menuitem" onClick={closeAccountMenu}>
                    <i className="bi bi-person" aria-hidden="true" />
                    <span>My Account</span>
                  </Link>
                  <Link to="/my-orders" className="ah-account-menu-item" role="menuitem" onClick={closeAccountMenu}>
                    <i className="bi bi-bag" aria-hidden="true" />
                    <span>Orders</span>
                  </Link>
                  <Link to="/my-addresses" className="ah-account-menu-item" role="menuitem" onClick={closeAccountMenu}>
                    <i className="bi bi-geo-alt" aria-hidden="true" />
                    <span>Addresses</span>
                  </Link>
                  <Link to="/wishlist" className="ah-account-menu-item" role="menuitem" onClick={closeAccountMenu}>
                    <i className="bi bi-heart" aria-hidden="true" />
                    <span>Wishlist</span>
                  </Link>
                  <button
                    type="button"
                    className="ah-account-menu-item ah-account-menu-item-danger"
                    role="menuitem"
                    onClick={async () => {
                      await handleLogout();
                      closeAccountMenu();
                    }}
                  >
                    <i className="bi bi-box-arrow-right" aria-hidden="true" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="ah-account-menu-item" role="menuitem" onClick={closeAccountMenu}>
                    <i className="bi bi-box-arrow-in-right" aria-hidden="true" />
                    <span>Sign in</span>
                  </Link>
                  <Link to="/signup" className="ah-account-menu-item ah-account-menu-item-primary" role="menuitem" onClick={closeAccountMenu}>
                    <i className="bi bi-person-plus" aria-hidden="true" />
                    <span>Create account</span>
                  </Link>
                </>
              )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile navigation drawer */}
      {isMobileMenuRendered && (
        <div className="ah-mobile-drawer" aria-hidden="true">
          <div
            className={`ah-mobile-drawer-backdrop ${isMobileMenuClosing ? 'ah-mobile-drawer-backdrop--closing' : 'ah-mobile-drawer-backdrop--opening'}`}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <aside
            id="mobile-navigation-drawer"
            className={`ah-mobile-drawer-panel ${isMobileMenuClosing ? 'ah-mobile-drawer-panel--closing' : 'ah-mobile-drawer-panel--opening'}`}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            ref={mobileMenuRef}
            tabIndex={-1}
          >
            <div className="ah-mobile-drawer-header">
              <div className="ah-mobile-user">
                <div className="ah-mobile-user-avatar" aria-hidden="true">
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.name} className="ah-mobile-user-avatar-img" />
                  ) : (
                    <div className="ah-mobile-user-avatar-fallback">{user?.initials || 'U'}</div>
                  )}
                </div>
                <div className="ah-mobile-user-meta">
                  <div className="ah-mobile-user-name">{user?.name || 'Account'}</div>
                </div>
              </div>
              <button type="button" className="ah-mobile-drawer-close" onClick={closeMobileMenu} aria-label="Close menu">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>

            <nav className="ah-mobile-drawer-nav" aria-label="Mobile navigation">
              <div className="ah-mobile-nav-group" aria-label="Main navigation">
                <Link
                  to="/products"
                  className="ah-mobile-nav-item"
                  onClick={closeMobileMenu}
                >
                  All Products
                </Link>
              </div>

              <div className="ah-mobile-nav-divider" role="separator" />

              {isAuthenticated ? (
                <>
                  <div className="ah-mobile-nav-group" aria-label="Account">
                    <Link to="/profile" className="ah-mobile-nav-item" onClick={closeMobileMenu}>
                      Profile
                    </Link>
                    <Link to="/my-orders" className="ah-mobile-nav-item" onClick={closeMobileMenu}>
                      Orders
                    </Link>
                    <Link to="/my-addresses" className="ah-mobile-nav-item" onClick={closeMobileMenu}>
                      Addresses
                    </Link>
                    <Link to="/wishlist" className="ah-mobile-nav-item" onClick={closeMobileMenu}>
                      Wishlist
                    </Link>
                    <button type="button" className="ah-mobile-nav-item ah-mobile-nav-item-button" onClick={async () => {
                      await handleLogout();
                      closeMobileMenu();
                    }}>
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="ah-mobile-nav-group" aria-label="Account">
                  <Link to="/login" className="ah-mobile-nav-item" onClick={closeMobileMenu}>
                    Login
                  </Link>
                  <Link to="/signup" className="ah-mobile-nav-item ah-mobile-nav-item-primary" onClick={closeMobileMenu}>
                    Sign Up
                  </Link>
                </div>
              )}

              <div className="ah-mobile-nav-divider" role="separator" />
            </nav>
          </aside>
        </div>
      )}

      <CartDrawer isOpen={showCartDrawer} onClose={() => setShowCartDrawer(false)} />
    </header>
  );
}

export default Header;

